/**
 * chatHistoryWiring — composes the Phase 1 modules (projection,
 * store) into the React hook the Variant C shell uses to read +
 * grow per-document chat history.
 *
 * The shell never imports projection / store directly — it imports
 * the hook from here and a couple of thin helpers. That keeps the
 * integration surface narrow: refactors of the projection/store
 * contracts ripple through ONE wiring file rather than many shell
 * call sites.
 *
 * SRP: glue. Pure helpers stay pure (testable in isolation); the
 * hook stitches them with React state + effects.
 *
 * # DECISION: A new message lands in BOTH localStorage AND a local
 * #   React state slice on every EventLog append.
 * # WHY: localStorage is write-only from React's perspective — there
 * #      is no built-in subscription. If the view re-read the store
 * #      on every render, we'd burn CPU on JSON.parse for every
 * #      eventlog change. Mirroring writes into a state slice gives
 * #      the view a stable React-side source of truth. The store is
 * #      the durability layer; the state slice is the rendering
 * #      layer; the projection is the bridge. Each layer has one job.
 * # DATE: 2026-04-26
 *
 * # DECISION: The hook returns a FLAT ascending message list plus
 * #   backward-pagination state — not time buckets. Day grouping is
 * #   presentation (chatDayGrouping.ts, consumed by TimelineView).
 * # WHY: the timeline renders one continuous chronological stream
 * #      with sticky day banners; pagination prepends older pages
 * #      into the same flat list. Keeping grouping out of the wiring
 * #      lets the view re-group by "now" on render without the hook
 * #      re-deriving state.
 * # DATE: 2026-07-14
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { EventLogEntry } from "./eventLog";
import { type ChatMessage, projectToMessages } from "./messageProjection";
import type { ChatHistoryPartition } from "./services/chatHistoryStore";
import {
  CHAT_PAGE_SIZE,
  fetchDocumentChat,
  fetchRunChat,
  scheduleChatSync,
} from "./services/documentSessionService";

// ---------------------------------------------------------------------------
// Pure helper — tested in isolation
// ---------------------------------------------------------------------------

/**
 * Minimum-information projection of the shell's selection object.
 * The wiring depends on this shape, NOT on LoomFreshSelection — so
 * a future selection refactor doesn't ripple into this module.
 */
export interface PartitionableSelection {
  libraryId: string;
  workspaceId?: string | null;
  docCode: string;
  docName: string;
}

/**
 * Derive a ChatHistoryPartition from a selection-shaped object.
 * Returns null when the selection is missing or any required field
 * is empty. Pure.
 *
 * Empty-string fields fall through to null because partitionKey()
 * would otherwise build a key like ``…|||…`` that collides with
 * other half-empty selections — silent cross-history bleed.
 */
export function partitionFromSelection(
  selection: PartitionableSelection | null | undefined,
): ChatHistoryPartition | null {
  if (!selection) return null;
  if (!selection.libraryId || !selection.docCode || !selection.docName) {
    return null;
  }
  return {
    libraryId: selection.libraryId,
    // Convert null → undefined so the partition key derivation
    // (which uses ``?? ""``) sees a single empty-slot representation.
    workspaceId: selection.workspaceId ?? undefined,
    docCode: selection.docCode,
    docName: selection.docName,
  };
}

/**
 * Pure helper used by both the hook and tests: project a slice of
 * EventLog entries into the chat-message stream. Wraps the
 * projection with the slice arithmetic so callers don't reinvent
 * the "since last seen" pattern.
 *
 * Returns the new messages in chronological order. The cursor
 * (``previousLength``) is the entries-array length the caller last
 * processed; everything beyond it is "new".
 */
export function projectNewEntries(entries: readonly EventLogEntry[], previousLength: number): ChatMessage[] {
  if (entries.length <= previousLength) return [];
  const out: ChatMessage[] = [];
  for (let i = previousLength; i < entries.length; i++) {
    for (const msg of projectToMessages(entries[i])) {
      out.push(msg);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Hook — composed of: load → mirror new EventLog entries → bucket
// ---------------------------------------------------------------------------

export interface UseChatHistoryViewResult {
  /** Flat chronological (ascending ``at``) persisted + live-mirrored
   *  messages for the current doc/run scope. Null when no document is
   *  selected or no messages exist yet — preserves the old "no view"
   *  contract for empty-state rendering. */
  messages: ChatMessage[] | null;
  /** True when older pages remain on the backend before the oldest
   *  loaded message. */
  hasMoreOlder: boolean;
  /** True while a ``loadOlder`` fetch is in flight. */
  isLoadingOlder: boolean;
  /** Fetch the next older page (``CHAT_PAGE_SIZE``) and prepend it.
   *  Idempotent while in flight; no-op when ``hasMoreOlder`` is false. */
  loadOlder: () => Promise<void>;
}

// Dedup key — ``(at, role)`` rather than ``at`` alone so paired
// messages dispatched in the same millisecond (e.g. the user prompt
// + agent_note reply from useChatSystem.handleSendChat's suggestion
// path) don't overwrite each other in the merge map.
// Date.now() resolution is 1 ms and back-to-back synchronous calls
// routinely return the same value, so an ``at``-only key drops one
// of the pair.
// Tool rows additionally carry their name — a chat turn can resolve
// two different tool steps in the same millisecond, and an (at, role)
// key would silently drop one activity box.
// Mirrors timelineDedupKey in TimelineView — keep the two in sync.
function mergeKey(message: ChatMessage): string {
  const base = `${message.at}:${message.role}`;
  return message.role === "tool" ? `${base}:${message.name}` : base;
}

/**
 * Merge two ChatMessage arrays, deduping on ``(at, role)``. Exported
 * for tests; the hook is the only production caller.
 */
export function mergeMessages(existing: readonly ChatMessage[], incoming: readonly ChatMessage[]): ChatMessage[] {
  const byKey = new Map<string, ChatMessage>();
  for (const message of existing) byKey.set(mergeKey(message), message);
  for (const message of incoming) byKey.set(mergeKey(message), message);
  return [...byKey.values()].sort((a, b) => a.at - b.at);
}

/**
 * Read the chat history for ``partition`` and keep it in sync with
 * new EventLog entries.
 *
 * Behaviour:
 *   - Seeds from ``initialMessages`` on mount + on doc/run/seed change.
 *   - On every EventLog append, projects the new entries into chat
 *     messages, mirrors them into local React state, and appends the
 *     genuinely-new ones to the backend (debounced).
 *   - ``loadOlder`` pages backward through persisted history using the
 *     oldest loaded message as the cursor, prepending each page.
 *
 * ``runId`` scopes the rendered chat to a single run plus its
 * session-level history (entries with ``runId === null`` — greeting,
 * library/doctype selection, anything authored before a run was
 * active). Switching runId resets the in-memory store and re-seeds
 * from ``initialMessages`` so the caller (typically the shell's
 * ``handleRunLoaded``) controls the persisted seed via
 * ``setPersistedChatMessages``.
 *
 * The hook is intentionally tolerant of missing partitions —
 * callers can pass ``null`` while no document is selected and the
 * hook degrades gracefully (returns ``{ messages: null }``).
 */
export function useChatHistoryView(
  docId: string | null | undefined,
  entries: readonly EventLogEntry[],
  initialMessages: readonly ChatMessage[],
  runId: string | null = null,
  initialHasMore = false,
): UseChatHistoryViewResult {
  // The full set of messages we know about for this document.
  // Initialized from the backend hydrate seed; appended-to on
  // EventLog growth, prepended-to by loadOlder. Distinct from
  // ``entries`` so the view doesn't re-derive on every TimelineView
  // re-render.
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const initialMessagesSig =
    initialMessages.length === 0
      ? "0"
      : `${initialMessages.length}:${initialMessages[0]?.at ?? ""}:${initialMessages[initialMessages.length - 1]?.at ?? ""}`;

  // Cursor — the entries-array length we have already projected.
  // useRef so persisting it doesn't trigger a re-render.
  const cursorRef = useRef<number>(0);

  // Keys (``mergeKey``) of every message known to exist on the backend —
  // seeded, paged-in, or already scheduled for append. The mirror effect
  // consults this before scheduleChatSync so a re-seed (run switch resets
  // ``cursorRef`` and re-projects the WHOLE EventLog) doesn't re-append
  // the session's messages to Mongo. Re-projected messages carry no
  // ``_id``, so the server's ``_id`` dedupe cannot catch them — this set
  // is the only thing standing between a run switch and duplicate rows.
  // Accumulates for the lifetime of a document (reset only on docId
  // change): a full reset on re-seed would re-schedule any live message
  // that hadn't round-tripped into the seed fetch yet.
  const syncedKeysRef = useRef<{ docId: string | null; keys: Set<string> }>({
    docId: null,
    keys: new Set(),
  });

  // In-flight guard as a ref — IntersectionObserver callbacks can fire
  // faster than the ``isLoadingOlder`` state commit.
  const loadingOlderRef = useRef(false);

  // Load on document/run/seed change. Including runId in the deps
  // means switching runs (Drafts panel load, RUN_STARTED, RUN_RESTORED)
  // re-seeds the visible messages from whatever ``initialMessages`` the
  // shell has fetched for the new run. The seed is also filtered to the
  // current scope so a stale ``persistedChatMessages`` from the previous
  // run doesn't bleed in for the brief window before the shell refetches.
  //
  // Trigger on initialMessagesSig (content hash) rather than the raw
  // array reference — the shell sometimes rebuilds the array identity
  // (re-fetch, memo invalidation) without changing its scoped content,
  // and a raw-reference dep would wipe + re-seed on every such ripple.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  // — initialMessages/initialHasMore are read but the signature is the trigger.
  useEffect(() => {
    cursorRef.current = 0;
    if (syncedKeysRef.current.docId !== (docId ?? null)) {
      syncedKeysRef.current = { docId: docId ?? null, keys: new Set() };
    }
    if (!docId) {
      setMessages([]);
      setHasMoreOlder(false);
      return;
    }
    const scoped = initialMessages.filter(
      (m) => m.run_id === runId || m.run_id === null || m.run_id === undefined,
    );
    const seeded = mergeMessages([], scoped);
    for (const m of seeded) syncedKeysRef.current.keys.add(mergeKey(m));
    setMessages(seeded);
    setHasMoreOlder(initialHasMore);
  }, [docId, runId, initialMessagesSig, initialHasMore]);
  // Mirror new EventLog entries into in-memory messages and sync the
  // genuinely-new ones back to the backend so it remains the source
  // of truth.
  useEffect(() => {
    if (!docId) return;
    if (entries.length < cursorRef.current) {
      // Entries shrank (run reset / eventlog re-init). Re-baseline.
      cursorRef.current = entries.length;
      return;
    }
    const projected = projectNewEntries(entries, cursorRef.current);
    cursorRef.current = entries.length;
    // Filter to messages that belong on the current run's scroll:
    // either tagged with this runId, or session-level (run_id null /
    // absent — greeting, library/doctype selection, etc.). Messages
    // belonging to other runs are still persisted (the EventLog stamp
    // happens at append time), but they don't render here.
    const newMessages = projected.filter(
      (m) => m.run_id === runId || m.run_id === null || m.run_id === undefined,
    );
    if (newMessages.length === 0) return;
    // Only messages the backend hasn't seen get appended — see
    // ``syncedKeysRef``. The state merge still takes ALL of them so a
    // re-projection after seed keeps the rendered list complete.
    const unsynced = newMessages.filter((m) => !syncedKeysRef.current.keys.has(mergeKey(m)));
    if (unsynced.length > 0) {
      for (const m of unsynced) syncedKeysRef.current.keys.add(mergeKey(m));
      scheduleChatSync(docId, unsynced, runId);
    }
    setMessages((prev) => mergeMessages(prev, newMessages));
    // Document/run change is handled by the load-effect above; this
    // effect listens to entries growth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, runId, entries]);

  // Backward pagination — fetch the page before the oldest loaded
  // message and prepend it. Cursor = the oldest message that has a
  // backend ``_id``; its ``at`` rides along as the server-side
  // fallback for evicted/re-minted ids. Reads only — never touches
  // ``cursorRef`` (EventLog projection) or scheduleChatSync.
  const loadOlder = useCallback(async (): Promise<void> => {
    if (!docId || loadingOlderRef.current || !hasMoreOlder) return;
    loadingOlderRef.current = true;
    setIsLoadingOlder(true);
    try {
      const oldestWithId = messages.find((m) => (m as { _id?: string })._id !== undefined);
      const opts = {
        limit: CHAT_PAGE_SIZE,
        before: (oldestWithId as { _id?: string } | undefined)?._id,
        beforeAt: messages[0]?.at,
      };
      const page = runId ? await fetchRunChat(runId, opts) : await fetchDocumentChat(docId, opts);
      // null = network failure / 404: keep hasMoreOlder so the scroll
      // sentinel can retry.
      if (page === null) return;
      if (page.messages.length > 0) {
        for (const m of page.messages) syncedKeysRef.current.keys.add(mergeKey(m));
        setMessages((prev) => mergeMessages(page.messages, prev));
      }
      setHasMoreOlder(page.hasMore);
    } finally {
      loadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [docId, runId, hasMoreOlder, messages]);

  return {
    messages: !docId || messages.length === 0 ? null : messages,
    hasMoreOlder,
    isLoadingOlder,
    loadOlder,
  };
}
